import { message, notification } from 'antd';
import React, { createContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { Stripe, StripeElements } from '@stripe/stripe-js';
import { Intent } from 'lib.store/src/main/payment/models';
import {
  useLazyGetOpenStripeQuery,
  usePostBillingMethodIdMutation,
  usePostDefaultBillingMethodIdMutation,
} from 'lib.store/src/main/payment/payment.api';
import sendAnalytics from 'lib.hooks/src/sendAnalytics/sendAnalytics';
import { GTMEventName } from 'lib.hooks/src/sendAnalytics/constants';
import { ErrorDataResponse } from 'lib.shared/src/utils';
import { billingActions } from 'lib.store/src/main/billing/billing.slice';
import { useDispatch } from 'react-redux';
import { userActions } from 'lib.store/src/main/-user/user.slice';
import { UserType } from 'lib.store/src/models-to-replace/auth';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'lib.hooks/src/redux';
import { useAuth } from './AuthContext';
import type { RootState } from 'lib.store/src/index';

interface StripeContextType {
  loading: boolean;
  onOpenStripeForm: () => void;
  onAddPaymentClick: (
    event?: { preventDefault: () => void },
    redirectTo?: string,
    isReFetchUser?: boolean,
  ) => Promise<void>;
  onCloseStripeForm: () => void;
  stripe: Stripe | null;
  setStripe: (info: Stripe | null) => void;
  // FROM STRIPE
  elements: StripeElements | null;
  setElements: (info: StripeElements | null) => void;
  intent: Intent | null;
  setIntent: (intent: Intent | null) => void;
  apiKey: string;
  setApiKey: (key: string) => void;
  selectedCardId: string;
  setSelectedCardId: (key: string) => void;
  isBillingExist: boolean;
}

export const StripeContext = createContext<StripeContextType>({
  loading: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onOpenStripeForm: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onAddPaymentClick: async (_event?: { preventDefault: () => void }, _redirectTo?: string) => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onCloseStripeForm: () => {},
  stripe: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setStripe: () => {},
  intent: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setIntent: () => {},
  apiKey: '',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setApiKey: () => {},
  elements: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setElements: () => {},
  selectedCardId: '',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setSelectedCardId: () => {},
  isBillingExist: false,
});

interface StripeProviderProps {
  children: ReactNode;
}

export const StripeProvider: React.FC<StripeProviderProps> = ({ children }) => {
  const { user: fetchedUser } = useAuth();
  const userState = useAppSelector((state: RootState) => state.userAuth.user);
  const user = userState || fetchedUser;

  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(false);
  const [intent, setIntent] = useState<Intent | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [elements, setElements] = useState<StripeElements | null>(null);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [selectedCardId, setSelectedCardId] = useState('');

  const [onOpenForm] = useLazyGetOpenStripeQuery();
  const [postDefault] = usePostDefaultBillingMethodIdMutation();
  const dispatch = useDispatch();
  const isAddCardMode = useAppSelector(
    (state: RootState) => state.billingState.flowMode === 'add_card',
  );

  const isBillingExist = user?.organization?.billing?.id;

  const onOpenStripeForm = useCallback(async () => {
    if (loading || (intent && elements)) {
      return;
    }

    setLoading(true);
    if (isAddCardMode) {
      dispatch(billingActions.resetFlowMode());
    }
    try {
      const response = await onOpenForm();
      if (response?.data?.data?.intent) {
        setLoading(false);
        setIntent(response.data.data.intent);
        setApiKey(response.data.data.api_key);
      }
    } catch (error) {
      console.error('rejected', error);
    } finally {
      setLoading(false);
    }
  }, [loading, intent, elements, isAddCardMode, dispatch, onOpenForm]);

  const onCloseStripeForm = useCallback(() => {
    setIntent(null);
    setApiKey('');
    setStripe(null);
    setElements(null);
    setSelectedCardId('');
    setLoading(false);
  }, []);

  const [postAddCard] = usePostBillingMethodIdMutation();

  const handlePaymentMethodUpdate = useCallback(
    async ({ id }: { id: string }) => {
      setLoading(true);
      try {
        const response = await postAddCard({ id });
        await postDefault({ id });

        setLoading(false);
        if ('data' in response) {
          setIntent(null);
          setSelectedCardId(id);
          notification.open({
            message: (
              <span>
                <b className="text-uppercase-first">
                  {response.data.data.brand} ending in {response.data.data.last4}
                </b>{' '}
                has been successfully added
              </span>
            ),
            placement: 'bottomLeft',
            closeIcon: false,
            type: 'success',
          });
          dispatch(
            userActions.patchUserState({
              ...user,
              organization: {
                ...user?.organization,
                billing: {
                  ...user?.organization?.billing,
                  payment_method_id: id,
                },
              },
            } as UserType),
          );
        }
        if ('error' in response) {
          setIntent(null);
          notification.open({
            message:
              (response as ErrorDataResponse).error.data.message || 'Error updating payment method',
            placement: 'bottomLeft',
            closeIcon: false,
            type: 'error',
          });
        }
      } catch (error) {
        setLoading(false);
        console.error('rejected', error);
        throw Error(error as string);
      }
    },
    [postAddCard, postDefault, user, dispatch],
  );

  const onAddPaymentClick = useCallback(
    async (event?: { preventDefault: () => void }, redirectTo?: string, _isReFetchUser = false) => {
      // We don't want to let default form submission happen here,
      // which would refresh the page.
      event && event.preventDefault();

      if (!stripe || !elements) {
        throw new Error('Stripe not initialized properly');
      }

      if (!intent || !intent.client_secret) {
        throw new Error('Setup intent not properly initialized');
      }

      // Submit elements first to trigger validation
      const { error: submitError } = await elements.submit();
      if (submitError) {
        void message.open({
          type: 'error',
          content: submitError.message || 'Validation failed',
        });
        throw new Error(submitError.message || 'Validation failed');
      }

      const { setupIntent, error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: 'https://example.com/order/123/complete',
        },
        clientSecret: intent.client_secret,
        redirect: 'if_required',
      });

      if (error && error?.message) {
        void message.open({
          type: 'error',
          content: error.message,
        });
        throw new Error(error.message);
      }

      if (setupIntent && setupIntent?.payment_method) {
        sendAnalytics(GTMEventName.enterCardData);
        return handlePaymentMethodUpdate({
          id: setupIntent.payment_method.toString(),
        }).then(() => {
          redirectTo ? window.location.replace(redirectTo) : null;
        });
      }

      throw new Error('Failed to process payment method');
    },
    [stripe, elements, handlePaymentMethodUpdate, intent],
  );

  useEffect(() => {
    if (intent) {
      onCloseStripeForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <StripeContext.Provider
      value={{
        loading,
        intent,
        setIntent,
        apiKey,
        setApiKey,
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onOpenStripeForm,
        onAddPaymentClick,
        onCloseStripeForm,
        stripe,
        setStripe,
        elements,
        setElements,
        selectedCardId,
        setSelectedCardId,
        isBillingExist: Boolean(isBillingExist),
      }}
    >
      {children}
    </StripeContext.Provider>
  );
};
